import { Record as record } from 'immutable';
import _ from 'lodash';
import { List } from 'immutable';

import { construct, fields, resolve, resolveAll } from '../../helpers/LinkMapper';
import DeliveryOption from '../DeliveryOption';
import Address from '../customer/Address';
import Photocard from '../Photocard';
import Station from '../Station';
import trainTime from '../../lib/TrainTime';
import { FulfilmentType } from '../../constants/AppConstants';
import FeatureFlagStore from '../../../src/stores/FeatureFlagStore';
import STicketDelivery from '../sticket/STicketDelivery';

const defaults = {
    id: null,
    deliveryMethodId: null,
    fulfilmentMethod: null,
    displayName: null,
    internalName: null,
    price: null,
    isTrackable: null,
    isSignedFor: null,
    isPrintedEticket: null,
    address: new Address(),
    photocards: new List(),
    sTicketPhotocard: new Photocard(),
    sTicketDelivery: new STicketDelivery(),
    storedAddress: null,
    collectionLocation: null,
    collectionStartDate: null,
    collectionWindowDays: 9,
    collectionPoints: null,
};

class OrderFulfilment extends record(defaults) {
    static fromJSON(json = {}) {
        return construct(OrderFulfilment, json, {
            id: fields.number,
            deliveryMethodId: fields.string,
            fulfilmentMethod: fields.string,
            displayName: fields.string,
            internalName: fields.string,
            price: fields.number,
            isTrackable: fields.bool,
            isSignedFor: fields.bool,
            isPrintedEticket: fields.bool,
            storedAddress: resolve(json.storedAddress).as(Address),
            address: resolve(json.address).as(Address) || new Address(),
            photocards: resolveAll(json.photocards).as(Photocard) || new List(),
            collectionLocation: resolve(json.collectionLocation).as(Station),
            collectionStartDate: resolve(json.collectionStartDate).with(trainTime),
            collectionWindowDays: resolve(json.collectionWindowDays).with(Number) - 1,
            collectionPoints: fields.bool,
        });
    }

    /*
     * Only return keys related to the selected delivery method
     */
    getPruned () {
        let additionalFields = {};

        if (this.isCollection()) {
            additionalFields = {
                ...(this.collectionLocation && { collectionLocation: this.collectionLocation.nlc }),
                collectionStartDate: this.collectionStartDate,
                collectionWindowDays: this.collectionWindowDays
            };
        } else if (this.isPostalDelivery()) {
            const enableETicketByPost = FeatureFlagStore.get('enableETicketByPost');
            additionalFields = {
                address: this.address ? this.address.id : null,
                isSignedFor: this.isSignedFor,
                ...(enableETicketByPost && { isPrintedEticket: this.isPrintedEticket }),
                isTrackable: this.isTrackable,
                price: this.price
            };
        }

        return {
            deliveryMethodId: this.deliveryMethodId,
            displayName: this.displayName,
            fulfilmentMethod: this.fulfilmentMethod,
            ...additionalFields
        };
    }

    getAddress () {
        return this.storedAddress || this.address;
    }

    requiresLocation () {
        return this.isCollection() || (this.isSmartcard() && this.collectionPoints);
    }

    hasFulfilmentMethod() {
        return Boolean(this.fulfilmentMethod);
    }

    isPrintAtHome() {
        return this.fulfilmentMethod === FulfilmentType.PRINT_AT_HOME;
    }

    isETicket() {
        return this.fulfilmentMethod === FulfilmentType.E_TICKET;
    }

    isSTicket() {
        return this.fulfilmentMethod === FulfilmentType.S_TICKET;
    }

    isCollection() {
        return this.fulfilmentMethod === FulfilmentType.COLLECTION;
    }

    isPostalDelivery() {
        return this.fulfilmentMethod === FulfilmentType.POSTAL;
    }

    isETicketByPost() {
        return this.fulfilmentMethod === FulfilmentType.POSTAL && this.isPrintedEticket;
    }

    isSmartcard() {
        return this.fulfilmentMethod === FulfilmentType.SMARTCARD;
    }

    hasCollectionLocation() {
        return !!this.collectionLocation;
    }

    hasSeasonTrip (trips) {
        return trips.some((trip) => trip.getSeasonFare());
    }

    numberOfSeasonTrips (trips) {
        return trips.filter((trip) => trip.getSeasonFare()).size;
    }

    isValid (trips = new List(), { disableAddressCheck = false, linkedSmartcards = false } = {}, smartCardDelivery) {
        if (this.hasSeasonTrip(trips) && !this.photocards.every(e => e.isComplete()) && !this.isSmartcard() && !this.isSTicket()) {
            return false; // TODO[limal] CORE-8307 no error message for user
        } else if (this.requiresLocation()) {
            const isValidCollection = !!this.collectionLocation;
            const enableSmartcardAssociations = FeatureFlagStore.get('enableSmartcardAssociations');

            if (this.isSmartcard()) {
                let isLinkedSmartcardsValid = true;
                if (enableSmartcardAssociations && linkedSmartcards) {
                    const tickets = trips.map(trip => trip.getProducts()).flatten(1).map(fare => fare.tickets).flatten(1);
                    isLinkedSmartcardsValid = tickets.size === linkedSmartcards.size;
                }

                return isLinkedSmartcardsValid && isValidCollection;
            }

            return isValidCollection;
        } else if (this.isPostalDelivery()) {
            if (this.storedAddress){
                return true;
            } else {
                return disableAddressCheck ? true : _.result(this.address, 'isComplete');
            }
        } else if (this.isPrintAtHome()) {
            return trips.every((trip) => trip.selfPrintPassenger.isComplete());
        } else if (this.isETicket()) {
            return true; // ticket is send to e-mail passed to payment form
        } else if (this.isSmartcard() && !smartCardDelivery.errors) {
            return true;
        } else if (this.isSTicket()) {
            return this.sTicketPhotocard.isComplete() && this.sTicketDelivery.isComplete();
        }

        return false;
    }

    setOption (option) {
        option = option || new DeliveryOption();
        let updated = this.merge({
            deliveryMethodId: option.id,
            fulfilmentMethod: option.fulfilmentMethod,
            displayName: option.displayName,
            internalName: option.internalName,
            price: option.price,
            isTrackable: option.isTrackable,
            isSignedFor: option.isSignedFor,
            collectionPoints: option.collectionPoints && option.collectionPoints.size > 0,
        });

        const enableETicketByPost = FeatureFlagStore.get('enableETicketByPost');

        if (enableETicketByPost) {
            updated = updated.set('isPrintedEticket', option.isPrintedEticket);
        }

        if (updated.requiresLocation()) {
            if (option.collectionPoints.size) {
                const stillAvailable = this.collectionLocation && !!option.collectionPoints.find((point) => point.nlc === this.collectionLocation.nlc);
                if (!stillAvailable) {
                    updated = updated.set('collectionLocation', option.suggestedCollectionPoint || null);
                }
            } else {
                updated = updated.set('collectionLocation', null);
            }
        }

        return updated;
    }
}

export default OrderFulfilment;



// WEBPACK FOOTER //
// ./src/records/order/Fulfilment.js